import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { supportAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/utils';
import { HelpCircle, Plus, MessageSquare, Send, CheckCircle, Clock } from 'lucide-react';

export default function Support() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [respondOpen, setRespondOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [form, setForm] = useState({ subject: '', message: '' });
  const [responseMsg, setResponseMsg] = useState('');
  const isAdmin = user?.role === 'platform_admin';

  const fetchTickets = async () => {
    try {
      const res = await supportAPI.getTickets();
      setTickets(res.data || []);
    } catch { setTickets([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTickets(); }, []);

  const handleCreate = async () => {
    if (!form.message.trim()) { toast.error('Message required'); return; }
    try {
      await supportAPI.createTicket(form);
      toast.success('Support ticket submitted');
      setCreateOpen(false);
      setForm({ subject: '', message: '' });
      fetchTickets();
    } catch { toast.error('Failed to submit'); }
  };

  const handleRespond = async () => {
    if (!selectedTicket || !responseMsg.trim()) return;
    try {
      await supportAPI.respond(selectedTicket.ticket_id, { message: responseMsg, status: 'responded' });
      toast.success('Response sent');
      setRespondOpen(false);
      setResponseMsg('');
      fetchTickets();
    } catch { toast.error('Failed'); }
  };

  const statusColors = { open: 'bg-blue-500', responded: 'bg-green-500', closed: 'bg-gray-500' };

  if (loading) return <Layout><div className="space-y-4"><Skeleton className="h-8 w-48" />{[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}</div></Layout>;

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="support-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight">Support</h1>
            <p className="text-muted-foreground text-sm mt-1">{isAdmin ? 'All support tickets' : 'Your support queries'}</p>
          </div>
          {!isAdmin && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-secondary hover:bg-secondary/90" data-testid="create-ticket-btn"><Plus className="mr-2 h-4 w-4" />New Query</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Submit Support Query</DialogTitle></DialogHeader>
                <div className="space-y-3 py-3">
                  <div><Label>Subject</Label><Input placeholder="Brief summary" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} data-testid="ticket-subject" /></div>
                  <div><Label>Message *</Label><Textarea placeholder="Describe your issue, request corrections, or ask a question..." value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={4} data-testid="ticket-message" /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                  <Button onClick={handleCreate} className="bg-secondary hover:bg-secondary/90" data-testid="submit-ticket-btn">Submit</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {tickets.length === 0 ? (
          <Card><CardContent className="py-12 text-center"><HelpCircle className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" /><p className="text-muted-foreground">{isAdmin ? 'No support tickets' : 'No queries yet. Need help? Submit a query!'}</p></CardContent></Card>
        ) : (
          <div className="space-y-3">
            {tickets.map(ticket => (
              <Card key={ticket.ticket_id} className="cursor-pointer hover:border-primary/30 transition-all" onClick={() => { setSelectedTicket(ticket); if (isAdmin) setRespondOpen(true); }} data-testid={`ticket-${ticket.ticket_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-[10px] ${statusColors[ticket.status] || 'bg-gray-500'}`}>{ticket.status}</Badge>
                        {isAdmin && <span className="text-xs text-muted-foreground">{ticket.user_name} ({ticket.user_email})</span>}
                      </div>
                      <h3 className="font-medium">{ticket.subject || 'General Query'}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{ticket.message}</p>
                      <span className="text-xs text-muted-foreground mt-2 block">{formatDate(ticket.created_at)}</span>
                    </div>
                    {ticket.responses?.length > 0 && (
                      <div className="flex items-center gap-1 text-xs text-green-500 shrink-0">
                        <MessageSquare className="h-3.5 w-3.5" />{ticket.responses.length}
                      </div>
                    )}
                  </div>
                  {/* Show responses */}
                  {ticket.responses?.length > 0 && (
                    <div className="mt-3 space-y-2 border-t pt-3">
                      {ticket.responses.map((r, i) => (
                        <div key={`${ticket.ticket_id}-resp-${r.created_at || i}`} className="bg-muted/50 rounded-lg p-3 text-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-xs">{r.responder_name}</span>
                            <span className="text-xs text-muted-foreground">{formatDate(r.created_at)}</span>
                          </div>
                          <p>{r.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Admin Response Dialog */}
        <Dialog open={respondOpen} onOpenChange={setRespondOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Respond to Ticket</DialogTitle></DialogHeader>
            {selectedTicket && (
              <div className="py-3">
                <div className="bg-muted/50 rounded-lg p-3 mb-4">
                  <p className="text-sm font-medium">{selectedTicket.user_name}: {selectedTicket.subject}</p>
                  <p className="text-sm text-muted-foreground mt-1">{selectedTicket.message}</p>
                </div>
                <div><Label>Your Response</Label><Textarea value={responseMsg} onChange={e => setResponseMsg(e.target.value)} rows={3} placeholder="Type your response..." data-testid="admin-response" /></div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setRespondOpen(false)}>Cancel</Button>
              <Button onClick={handleRespond} className="bg-secondary hover:bg-secondary/90" data-testid="send-response-btn"><Send className="mr-1 h-3.5 w-3.5" />Send</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
